import React from 'react';
import { Button, Card, Row, Col, Typography, Space, Divider, Tag, Collapse } from 'antd';
import { 
  TruckOutlined, 
  CheckCircleOutlined, 
  TeamOutlined, 
  DashboardOutlined, 
  SecurityScanOutlined, 
  RocketOutlined, 
  ArrowRightOutlined, 
  GlobalOutlined, 
  MobileOutlined, 
  BarChartOutlined,
  BranchesOutlined,
  BellOutlined,
  DollarCircleOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  CompassOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../utils/routes.config';
import { FaTruckLoading } from 'react-icons/fa';
import './landing.css';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const Landing = () => {
  const navigate = useNavigate();

  const metrics = [
    { value: '99.9%', label: 'Delivery Uptime', icon: <ThunderboltOutlined style={{ color: '#2563eb' }} /> },
    { value: '45 mins', label: 'Average Delivery Time', icon: <SyncOutlined style={{ color: '#10b981' }} /> },
    { value: '10,000+', label: 'Daily Shipments Handled', icon: <TruckOutlined style={{ color: '#f59e0b' }} /> },
    { value: '100%', label: 'Driver GPS Visibility', icon: <CompassOutlined style={{ color: '#8b5cf6' }} /> },
  ];

  const workflowSteps = [
    {
      step: '01',
      title: 'Auto Order Ingestion',
      desc: 'Seamless webhook & API synchronization captures incoming Shopify or custom store orders instantaneously.',
      tag: 'Shopify Integrated'
    },
    {
      step: '02',
      title: 'Smart Driver Dispatch',
      desc: 'Assign orders manually or via intelligent batch allocation based on driver availability, radius, and vehicle capacity.',
      tag: 'Multi-Warehouse'
    },
    {
      step: '03',
      title: 'Live Tracking & Route',
      desc: 'Drivers navigate through their dedicated app while customers receive live map tracking links with real-time ETA.',
      tag: 'GPS Enabled'
    },
    {
      step: '04',
      title: 'POD & Cash Reconciliation',
      desc: 'Instant digital signature, photo proof of delivery, and driver COD settlement tracking.',
      tag: 'Automated Accounting'
    }
  ];

  const features = [
    {
      icon: <TruckOutlined style={{ fontSize: 32, color: '#2563eb' }} />,
      title: 'Real-Time Driver Tracking',
      description: 'Accurate GPS monitoring of your fleet on live interactive maps. View active routes and delivery speed.',
      badge: 'Real-Time'
    },
    {
      icon: <DashboardOutlined style={{ fontSize: 32, color: '#10b981' }} />,
      title: 'Operations Dashboard',
      description: 'Centralized command center for managing pending orders, in-transit packages, driver status, and store metrics.',
      badge: 'Command Center'
    },
    {
      icon: <TeamOutlined style={{ fontSize: 32, color: '#8b5cf6' }} />,
      title: 'Fleet & Driver Management',
      description: 'Easily onboard drivers, monitor online/offline states, manage vehicle types, and broadcast emergency alerts.',
      badge: 'Fleet Ops'
    },
    {
      icon: <BranchesOutlined style={{ fontSize: 32, color: '#f59e0b' }} />,
      title: 'Multi-Hub & Warehouse Support',
      description: 'Configure multiple dispatch locations, warehouses, and pickup points with custom managers and geo-locations.',
      badge: 'Logistics'
    },
    {
      icon: <DollarCircleOutlined style={{ fontSize: 32, color: '#06b6d4' }} />,
      title: 'COD & Payment Reconciliation',
      description: 'Granular accounting tracking Cash on Delivery vs. Pre-paid orders collected driver by driver.',
      badge: 'Finance'
    },
    {
      icon: <SecurityScanOutlined style={{ fontSize: 32, color: '#ec4899' }} />,
      title: 'Enterprise API & Security',
      description: 'HMAC SHA256 authenticated REST endpoints with comprehensive Swagger and multi-language documentation.',
      badge: 'API-First'
    }
  ];

  const benefits = [
    'Zero manual dispatching delays with 1-click batch allocation',
    'Customer transparency via live branded tracking maps',
    'Driver accountability with digital proof of delivery (POD)',
    'Real-time automated SMS and push notifications',
    'Support for multiple regional warehouses and pickup hubs',
    'Comprehensive daily driver settlement & COD collection reports'
  ];

  const faqs = [
    {
      q: 'How does Magecomp Delivery Suite integrate with Shopify?',
      a: 'The suite connects directly via Webhooks and Shopify Admin APIs, syncing new orders instantly into your dispatch console as soon as a customer checks out.'
    },
    {
      q: 'Do drivers need an account to use the mobile app?',
      a: 'Yes, store administrators easily invite or register drivers from the Drivers management screen, granting them secure credentials for the mobile companion app.'
    },
    {
      q: 'Can I test the API before integrating into my custom ERP?',
      a: 'Absolutely! Our interactive API documentation is fully open and includes real-time examples in JavaScript, Python, PHP, and cURL.'
    },
    {
      q: 'What payment modes are supported for fleet orders?',
      a: 'The platform manages both Cash On Delivery (COD) driver collections and Pre-paid / Direct payment orders with automated daily reconciliation.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Top Navigation Bar */}
      <nav className="landing-navbar">
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
          <div 
            onClick={() => navigate(routes.HOME)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
          >
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 18,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
            }}>
              <FaTruckLoading />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                Magecomp
              </div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Delivery Suite
              </div>
            </div>
          </div>

          <Space size="middle" className="nav-actions">
            <Button 
              type="text" 
              onClick={() => navigate(routes.API_DOCUMENTATION)}
              style={{ fontWeight: 600, color: '#475569' }}
            >
              API Docs
            </Button>
            <Button 
              type="primary" 
              onClick={() => navigate(routes.LOGIN)}
              style={{ borderRadius: 8, fontWeight: 600, height: 38, padding: '0 20px', background: '#2563eb' }}
            >
              Sign In
            </Button>
          </Space>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="container">
            <Space direction="vertical" size="large" align="center" style={{ width: '100%', textAlign: 'center' }}>
              <div className="hero-badge">
                <RocketOutlined /> <span>Enterprise Delivery & Logistics Platform</span>
              </div>
              <Title level={1} className="hero-title">
                Next-Gen Delivery Operations for Modern E-Commerce
              </Title>
              <Paragraph className="hero-description">
                Centralize order dispatch, monitor drivers with real-time GPS tracking, and delight customers with live ETA maps. Built for multi-store and warehouse logistics.
              </Paragraph>
              <Space size="middle" wrap style={{ justifyContent: 'center', marginBottom: 24 }}>
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate(routes.LOGIN)}
                  className="cta-button"
                >
                  Access Dashboard
                </Button>
                <Button 
                  size="large" 
                  onClick={() => navigate(routes.API_DOCUMENTATION)}
                  className="cta-button-secondary"
                >
                  Explore Developer API
                </Button>
              </Space>

              {/* Showcase Banner Mockup */}
              <div className="hero-banner-wrapper">
                <div className="hero-banner-glow"></div>
                <img 
                  src="/image/hero-banner.jpg" 
                  alt="Delivery Management Platform Showcase" 
                  className="hero-banner-img"
                />
              </div>
            </Space>
          </div>
        </div>
      </section>

      {/* Metrics Ticker */}
      <section className="metrics-section">
        <div className="container">
          <Row gutter={[20, 20]} justify="center">
            {metrics.map((m, idx) => (
              <Col xs={12} sm={12} md={6} key={idx}>
                <div className="metric-box">
                  <div style={{ fontSize: 24, marginBottom: 6 }}>{m.icon}</div>
                  <div className="metric-value">{m.value}</div>
                  <div className="metric-label">{m.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="workflow-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <Tag color="blue" style={{ marginBottom: 8, padding: '4px 12px', fontSize: 13, borderRadius: 20 }}>
              Seamless Flow
            </Tag>
            <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>
              How The Delivery Pipeline Operates
            </Title>
            <Paragraph style={{ fontSize: 16, color: '#64748b', maxWidth: 640, margin: '8px auto 0' }}>
              From the instant an order is placed to customer doorstep signature and cash collection.
            </Paragraph>
          </div>

          <Row gutter={[24, 24]}>
            {workflowSteps.map((ws, i) => (
              <Col xs={24} sm={12} lg={6} key={i}>
                <div className="step-card">
                  <div className="step-number">{ws.step}</div>
                  <Tag color="processing" style={{ alignSelf: 'flex-start', marginBottom: 12 }}>
                    {ws.tag}
                  </Tag>
                  <Title level={4} style={{ margin: '0 0 8px 0', fontSize: 17, color: '#0f172a' }}>
                    {ws.title}
                  </Title>
                  <Paragraph style={{ color: '#64748b', fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>
                    {ws.desc}
                  </Paragraph>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 54 }}>
            <Tag color="cyan" style={{ marginBottom: 8, padding: '4px 12px', fontSize: 13, borderRadius: 20 }}>
              End-To-End Control
            </Tag>
            <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>
              Everything You Need to Scale Delivery
            </Title>
            <Paragraph style={{ fontSize: 16, color: '#64748b', maxWidth: 620, margin: '8px auto 0' }}>
              Designed to optimize dispatch times, eliminate delivery failure, and boost customer retention.
            </Paragraph>
          </div>

          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card 
                  className="feature-card"
                  hoverable
                  styles={{ body: { padding: '28px 24px' } }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <div className="feature-icon-wrapper">
                      {feature.icon}
                    </div>
                    <Tag color="geekblue">{feature.badge}</Tag>
                  </div>
                  <Title level={4} style={{ marginBottom: 10, fontSize: 17, color: '#0f172a' }}>
                    {feature.title}
                  </Title>
                  <Paragraph style={{ color: '#64748b', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <Tag color="green" style={{ marginBottom: 12, padding: '4px 12px', fontSize: 13, borderRadius: 20 }}>
                High ROI
              </Tag>
              <Title level={2} style={{ marginBottom: 16, fontWeight: 800, color: '#0f172a' }}>
                Why Leading E-Commerce Merchants Trust Magecomp
              </Title>
              <Paragraph style={{ fontSize: 15.5, color: '#64748b', marginBottom: 28, lineHeight: 1.6 }}>
                Replace chaotic spreadsheets and WhatsApp dispatching with automated, reliable delivery software tailored to regional logistics.
              </Paragraph>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {benefits.map((benefit, index) => (
                  <div key={index} className="benefit-item">
                    <CheckCircleOutlined style={{ color: '#10b981', fontSize: 20, marginRight: 14, flexShrink: 0 }} />
                    <Text style={{ fontSize: 15, color: '#334155', fontWeight: 500 }}>{benefit}</Text>
                  </div>
                ))}
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  top: '10%',
                  left: '10%',
                  right: '10%',
                  bottom: '10%',
                  background: 'radial-gradient(circle, rgba(37, 99, 235, 0.25) 0%, transparent 70%)',
                  filter: 'blur(30px)',
                  zIndex: 0,
                  pointerEvents: 'none'
                }}></div>

                <div style={{
                  position: 'relative',
                  zIndex: 1,
                  borderRadius: 20,
                  overflow: 'hidden',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.15)',
                  background: '#ffffff'
                }}>
                  <img 
                    src="/image/delivery-experience.jpg" 
                    alt="Seamless Doorstep Proof of Delivery" 
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'cover'
                    }}
                  />
                  
                  {/* Floating Digital POD Badge */}
                  <div style={{
                    position: 'absolute',
                    bottom: 20,
                    left: 20,
                    right: 20,
                    background: 'rgba(15, 23, 42, 0.85)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 12,
                    padding: '14px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    color: '#ffffff'
                  }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                        Real-Time Verification
                      </div>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: '#f8fafc', marginTop: 2 }}>
                        Digital Signatures & Instant POD
                      </div>
                    </div>
                    <div style={{
                      padding: '6px 12px',
                      background: 'rgba(16, 185, 129, 0.2)',
                      border: '1px solid rgba(16, 185, 129, 0.4)',
                      borderRadius: 20,
                      color: '#34d399',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <CheckCircleOutlined /> Delivered
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="faq-section">
        <div className="container" style={{ maxWidth: 860 }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <Tag color="purple" style={{ marginBottom: 8, padding: '4px 12px', fontSize: 13, borderRadius: 20 }}>
              Got Questions?
            </Tag>
            <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>
              Frequently Asked Questions
            </Title>
          </div>

          <Collapse 
            defaultActiveKey={['0']} 
            bordered={false} 
            className="custom-faq-collapse"
            expandIconPosition="end"
          >
            {faqs.map((faq, idx) => (
              <Panel 
                header={<span style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>{faq.q}</span>} 
                key={idx}
                style={{
                  background: '#ffffff',
                  marginBottom: 12,
                  borderRadius: 10,
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden'
                }}
              >
                <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, margin: 0 }}>
                  {faq.a}
                </p>
              </Panel>
            ))}
          </Collapse>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <Space direction="vertical" size="large" align="center" style={{ width: '100%', textAlign: 'center' }}>
              <div style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2563eb',
                fontSize: 24
              }}>
                <RocketOutlined />
              </div>
              <Title level={2} style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>
                Ready to Upgrade Your Delivery Operations?
              </Title>
              <Paragraph style={{ fontSize: 16, color: '#64748b', maxWidth: 580, margin: 0, lineHeight: 1.6 }}>
                Sign in to launch your delivery dashboard, configure warehouses, and connect your fleet of drivers in minutes.
              </Paragraph>
              <Space size="middle" wrap style={{ justifyContent: 'center' }}>
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate(routes.LOGIN)}
                  style={{ height: 48, fontSize: 15, padding: '0 28px', borderRadius: 8, fontWeight: 600, background: '#2563eb' }}
                >
                  Start Now
                </Button>
                <Button 
                  size="large" 
                  onClick={() => navigate(routes.API_DOCUMENTATION)}
                  style={{ height: 48, fontSize: 15, padding: '0 24px', borderRadius: 8, fontWeight: 600 }}
                >
                  Read API Guide
                </Button>
              </Space>
            </Space>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <Row gutter={[40, 32]}>
            <Col xs={24} sm={12} md={8}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 16
                }}>
                  <FaTruckLoading />
                </div>
                <span style={{ fontWeight: 700, fontSize: 16, color: '#f8fafc' }}>
                  Magecomp Delivery Suite
                </span>
              </div>
              <Paragraph style={{ color: '#94a3b8', fontSize: 13.5, lineHeight: 1.6 }}>
                Complete delivery management solution for merchants, dispatchers, drivers, and consumers.
              </Paragraph>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ fontWeight: 700, color: '#f8fafc', fontSize: 14, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Quick Navigation
              </div>
              <Space direction="vertical" size={10}>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate(routes.LOGIN); }}>Sign In to Dashboard</a>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate(routes.API_DOCUMENTATION); }}>Developer API Documentation</a>
                <a href="#" onClick={(e) => { e.preventDefault(); navigate("/live-map"); }}>Customer Live Map</a>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ fontWeight: 700, color: '#f8fafc', fontSize: 14, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Direct Support
              </div>
              <Paragraph style={{ color: '#94a3b8', fontSize: 13.5, lineHeight: 1.6 }}>
                Phone:{' '}
                <a href="tel:+917984802905" style={{ color: '#60a5fa', fontWeight: 600 }}>
                  +91 79848 02905
                </a>
                <br />
                Available Monday to Saturday for onboarding & fleet assistance.
              </Paragraph>
            </Col>
          </Row>
          <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.1)', margin: '32px 0 20px' }} />
          <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13 }}>
            © {new Date().getFullYear()} Magecomp Delivery Operations Platform. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
;

